// The viewport-width questions the dashboard's TS asks, three of them:
//  - is this a phone (`sm`)? The `ModelPicker` (#5210) and `Select` answer a phone
//    with the bottom sheet and a wider screen with the anchored popover, and CSS
//    cannot swap WHICH component mounts;
//  - is the fixed rail showing (`lg`)? `AppSidebar` closes its mobile drawer the
//    moment it is, because the drawer's menu and close buttons are `display: none`
//    from `lg` and a drawer left open would hold Tab with no way out (19-4).
//
// The widths are the shared `$breakpoints` map's (`packages/ui/styles/_tokens.scss`),
// never a second number: `viewport.test.ts` pins each to that map, because
// `scripts/media-query-discipline.test.ts` reads `.scss` only and cannot see a
// `matchMedia` here.

import { createSignal, onCleanup } from 'solid-js';

/** The `sm` breakpoint's min-width, in CSS px. */
export const SM_MIN_WIDTH_PX = 640;

/** The `md` breakpoint's min-width, in CSS px. */
export const MD_MIN_WIDTH_PX = 768;

/** The `lg` breakpoint's min-width, in CSS px. */
export const LG_MIN_WIDTH_PX = 1024;

/**
 * Reactive "at least `px` wide", following resizes and rotations. `undefined`
 * where `matchMedia` does not exist (SSR): each caller states its own default.
 * Call in a component body — the listener is removed with it.
 */
function createMinWidth(px: number): (() => boolean) | undefined {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
  const media = window.matchMedia(`(min-width: ${px}px)`);
  const [matches, setMatches] = createSignal(media.matches);
  const onChange = (e: MediaQueryListEvent): void => {
    setMatches(e.matches);
  };
  media.addEventListener('change', onChange);
  onCleanup(() => media.removeEventListener('change', onChange));
  return matches;
}

/**
 * Reactive "narrower than `sm`". `false` where `matchMedia` does not exist (a
 * test DOM, SSR): the wide layout is the default.
 */
export function createNarrowViewport(): () => boolean {
  const atSm = createMinWidth(SM_MIN_WIDTH_PX);
  return atSm === undefined ? () => false : () => !atSm();
}

/**
 * Reactive "at `lg` or wider" — the fixed rail is showing. `false` where
 * `matchMedia` does not exist.
 */
export function createRailViewport(): () => boolean {
  return createMinWidth(LG_MIN_WIDTH_PX) ?? (() => false);
}

/**
 * Reactive "at `md` or wider" — the Machines List view's table fits. `true` where
 * `matchMedia` does not exist: the wide layout is the default.
 */
export function createTableViewport(): () => boolean {
  return createMinWidth(MD_MIN_WIDTH_PX) ?? (() => true);
}
